<script setup lang="ts">
import type { Contact } from '@/api-swagger'
import type { FilterModel } from 'ag-grid-community'
import type { ColumnConfig } from '@/components/data-visualization/grids/ColumnSelector.vue'
import { computed, markRaw, ref, watch } from 'vue'
import { useToast } from 'vue-toast-notification'
import ColumnSelector from '@/components/data-visualization/grids/ColumnSelector.vue'
import GridVue from '@/components/data-visualization/grids/GridVue.vue'
import TodoModal from '@/components/features/todos/TodoModal.vue'
import CompanyLinkCell from '@/components/features/contact/CompanyLinkCell.vue'
import ContactGridActions from '@/components/features/contact/ContactGridActions.vue'
import { XMarkIcon, TrashIcon, MagnifyingGlassIcon, FunnelIcon } from '@heroicons/vue/24/outline'
import ButtonDaisy from '@/components/common/buttons/ButtonDaisy.vue'
import ConfirmModal from '@/components/common/layout/ConfirmModal.vue'
import Multiselect from 'vue-multiselect'
import 'vue-multiselect/dist/vue-multiselect.min.css'
import '@/assets/multiselect.css'
import { useContactStore } from '@/stores/useContactStore'

const $toast = useToast()
const contactStore = useContactStore()

const selectedContact = ref<Contact | null>(null)
const showModalTodo = ref(false)
const showDeleteConfirm = ref(false)
const contactToDelete = ref<{uuid: string; email: string;} | null>(null)
const isDeleting = ref(false)

const props = defineProps<{
  rows: any[]
  filterModel?: FilterModel
  useServerSidePagination?: boolean
  paginationPageSize?: number
  paginationTotalRows?: number
  currentPage?: number
  totalPages?: number
  isLoading?: boolean
  partnerId?: number | boolean
  allowSorting?: boolean
  showCompaniesFilter?: boolean
  allowFiltering?: boolean
  allowExport?: boolean
}>()

const emits = defineEmits<{
  (e: 'updateFilter', filterModel: FilterModel): void
  (e: 'updateSort', sortModel: { colId: string; sort: 'asc' | 'desc' } | null): void
  (e: 'updateSearch', search: string): void
  (e: 'updateCompanies', companyUuids: string[]): void
  (e: 'updateSigner', isSigner: boolean | undefined): void
  (e: 'clearFilters'): void
  (e: 'paginationChanged', limit: number, page: number): void
  (e: 'openModalEdit', contact: Contact): void
  (e: 'deleted'): void
}>()

// Configuration de toutes les colonnes possibles pour le selector
const allColumns = computed<ColumnConfig[]>(() => [
  { field: 'active', headerName: 'Actif', visible: true },
  { field: 'civility', headerName: 'Civilité', visible: true },
  { field: 'firstname', headerName: 'Prénom', visible: true },
  { field: 'lastname', headerName: 'Nom', visible: true },
  { field: 'isSigner', headerName: 'Signataire', visible: true },
  { field: 'function', headerName: 'Fonction', visible: true },
  {
    field: 'company',
    headerName: 'Entreprise',
    visible: typeof (props.partnerId) !== 'number',
    locked: typeof (props.partnerId) !== 'number'
  }
])

const visibleColumnFields = ref<string[]>(allColumns.value.filter(c => c.visible).map(c => c.field))

function onColumnsChanged(newVisibleColumns: string[]) {
  visibleColumnFields.value = newVisibleColumns
}

// Colonnes ag-grid en fonction des champs visibles
const gridColumns = computed(() => {
  const visibleFields = new Set(visibleColumnFields.value)
  return [
    {
      field: 'active',
      headerName: 'Statut',
      cellClass: 'border-0',
      hide: !visibleFields.has('active'),
      sortable: props.allowSorting,
      filter: false,
      cellRenderer: (params: any) => {
        const active = params.value
        const colorClass = active !== false ? 'badge-success' : 'badge-error'
        const text = active !== false ? 'Actif' : 'Archivé'
        return `<span class="badge ${colorClass} badge-sm text-white">${text}</span>`
      }
    },
    {
      field: 'civility',
      headerName: 'Civilité',
      cellClass: 'border-0',
      hide: !visibleFields.has('civility'),
      sortable: false,
      filter: false
    },
    {
      field: 'firstname',
      headerName: 'Prénom',
      cellClass: 'border-0',
      hide: !visibleFields.has('firstname'),
      sortable: props.allowSorting,
      filter: false
    },
    {
      field: 'lastname',
      headerName: 'Nom',
      cellClass: 'border-0',
      hide: !visibleFields.has('lastname'),
      sortable: props.allowSorting,
      filter: false
    },
    {
      field: 'isSigner',
      headerName: 'Signataire',
      cellClass: 'border-0',
      hide: !visibleFields.has('isSigner'),
      sortable: props.allowSorting,
      filter: false
    },
    {
      field: 'function',
      headerName: 'Fonction',
      cellClass: 'border-0',
      hide: !visibleFields.has('function'),
      sortable: false,
      filter: false
    },
    {
      field: 'company.name',
      headerName: 'Entreprise',
      cellClass: 'border-0',
      hide: !props.partnerId ? !visibleFields.has('company') : true,
      valueGetter: (params: any) => params.data?.parentId?.name ?? params.data?.company?.name ?? '',
      cellRenderer: typeof props.partnerId !== 'number' ? markRaw(CompanyLinkCell) : undefined,
      sortable: props.allowSorting,
      filter: false
    },
    {
      field: 'actions',
      headerName: 'Actions',
      resizable: false,
      filter: false,
      minWidth: 340,
      maxWidth: 340,
      cellRenderer: ContactGridActions,
      cellRendererParams: {
        onClickBtnDelete: (params: any) => {
          handleDeleteContact(params)
        },
        onClickNewTodo: (params: any) => {
          openModalTodo(params)
        },
        tooltipPosition: 'left'
      },
      sortable: false,
      cellClass: 'border-0 grid-action '
    }
  ]
})

function handleFilterChanged(filterModel: FilterModel) {
  console.log(filterModel)
  emits('updateFilter', filterModel)
}

function handleSortChanged(sortModel: any) {
  emits('updateSort', sortModel)
}

const searchQuery = ref('')

function handleSearchInput() {
  emits('updateSearch', searchQuery.value)
}

const selectedCompanies = ref<any[]>([])
const companyOptions = ref<any[]>([])
const signerFilter = ref<'none' | 'true' | 'false'>('none')

async function onSearchCompanies(query: string) {
  if (query.length < 3) {
    companyOptions.value = []
    return
  }
  const companies = await contactStore.searchCompanies(query)
  if (companies) {
    companyOptions.value = Array.isArray(companies) ? companies : []
  }
}

watch(selectedCompanies, (newVal) => {
  const uuids = newVal.map((c: any) => c.uuid)
  emits('updateCompanies', uuids)
  companyOptions.value = []
})

watch(signerFilter, (newVal) => {
  const value = newVal === 'none' ? undefined : newVal === 'true'
  emits('updateSigner', value)
})

function handleClearFilters() {
  searchQuery.value = ''
  selectedCompanies.value = []
  companyOptions.value = []
  signerFilter.value = 'none'

  emits('clearFilters')
}

function handlePaginationChanged(limit: number, page: number) {
  emits('paginationChanged', limit, page)
}

function openModalTodo(contact: any) {
  selectedContact.value = contact
  showModalTodo.value = true
}

function closeModal() {
  selectedContact.value = null
  showModalTodo.value = false
}

function handleTodoCreated() {
  selectedContact.value = null
  showModalTodo.value = false
}

function handleDeleteContact(contact: any) {
  if (contact) {
    contactToDelete.value = {uuid: contact.data.uuid, email: contact.data.email}
    showDeleteConfirm.value = true
  }
}

async function confirmDelete() {
  if (!contactToDelete.value) return
  isDeleting.value = true
  try {
    const payload = {
      active: false
    }
    const contactUuid = (contactToDelete.value as any).uuid
    const result = await contactStore.updateContact(contactUuid, payload)

    if (result.success) {
      $toast.success('Contact supprimé')
      emits('deleted')
      showDeleteConfirm.value = false
    } else {
      $toast.error(result.message || 'Erreur lors de la suppression du contact')
    }
  } catch (error) {
    $toast.error('Le contact ne peut pas être supprimé des éléments lui sont attachés')
  } finally {
    isDeleting.value = false
    contactToDelete.value = null
  }
}
</script>

<template>
  <GridVue
    :list="rows"
    :cols="gridColumns"
    :filter-model="filterModel"
    :use-server-side-pagination="useServerSidePagination"
    :pagination-page-size="paginationPageSize"
    :pagination-total-rows="paginationTotalRows"
    :current-page="currentPage"
    :total-pages="totalPages"
    :isLoading="isLoading"
    :showExport="allowExport"
    @onFilterChanged="handleFilterChanged"
    @onSortChanged="handleSortChanged"
    @onPaginationChanged="handlePaginationChanged"
  >
    <template #button>
      <slot name="button" />
    </template>
    <template #title>
      <h2 class="card-title">Liste des contacts</h2>
    </template>

    <template #filter>
      <div v-if="props.allowFiltering" class="flex flex-row items-end gap-4 flex-nowrap">
        <ButtonDaisy
          v-if="(filterModel && Object.keys(filterModel).length > 0) || searchQuery || (selectedCompanies && selectedCompanies.length > 0) || signerFilter !== 'none'"
          text="Effacer les filtres"
          variant="ghost"
          class="btn-sm text-error"
          :icon="XMarkIcon"
          @click="handleClearFilters"
        />

        <div class="dropdown dropdown-end">
          <div tabindex="0" class="btn btn-ghost btn-sm gap-2" role="button">
            <FunnelIcon class="w-5 h-5" />
            <span class="hidden sm:inline">Filtres</span>
          </div>
          <div
            tabindex="-1"
            class="dropdown-content z-[100] menu p-4 shadow-lg bg-base-100 rounded-box w-72 border border-base-300 space-y-4"
          >
            <div class="form-control">
              <label class="label pt-0">
                <span class="label-text font-semibold">Recherche</span>
              </label>
              <div class="relative">
                <div class="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none z-[1]">
                  <MagnifyingGlassIcon class="w-4 h-4 text-gray-400" />
                </div>
                <input
                  v-model="searchQuery"
                  type="text"
                  class="input input-bordered input-sm w-full pl-10"
                  placeholder="Rechercher..."
                  @input="handleSearchInput"
                />
              </div>
            </div>

            <div class="form-control" v-if="typeof props.partnerId !== 'number' && props.showCompaniesFilter">
              <label class="label pt-0">
                <span class="label-text font-semibold">Société</span>
              </label>
              <Multiselect
                v-model="selectedCompanies"
                :options="companyOptions"
                :multiple="true"
                :searchable="true"
                :internal-search="false"
                placeholder="Filtrer par société"
                label="name"
                track-by="uuid"
                :block-keys="['Delete']"
                @search-change="onSearchCompanies"
              >
                <template #noOptions>
                  <div class="p-2 text-xs">Saisissez au moins 3 caractères</div>
                </template>
                <template #noResult>
                  <div class="p-2 text-xs">Aucun résultat trouvé</div>
                </template>
              </Multiselect>
            </div>

            <div class="form-control">
              <label class="label pt-0">
                <span class="label-text font-semibold">Signataire</span>
              </label>
              <select v-model="signerFilter" class="select select-bordered select-sm w-full font-normal">
                <option value="none">Les deux</option>
                <option value="true">Oui</option>
                <option value="false">Non</option>
              </select>
            </div>
          </div>
        </div>

        <ColumnSelector
          :columns="allColumns"
          cookie-name="contacts-columns-visibility"
          @columns-changed="onColumnsChanged"
        />
      </div>
    </template>
  </GridVue>

  <TodoModal
    v-if="selectedContact"
    :showModal="showModalTodo"
    mode="create"
    @close="closeModal"
    @created="handleTodoCreated"
    :contact="selectedContact"
  />

  <ConfirmModal
    :show-modal="showDeleteConfirm"
    title="Supprimer le contact"
    :body="`Êtes-vous sûr de vouloir supprimer le contact ${contactToDelete?.email} ?`"
    confirm-text="Oui"
    cancel-text="Non"
    confirm-variant="error"
    :confirm-icon="TrashIcon"
    :loading="isDeleting"
    @confirm="confirmDelete"
    @cancel="showDeleteConfirm = false"
  />
</template>
